import { Button, Icon } from '@alto-avios/alto-ui'
import { CabinClass, FlightResult } from '@reward-platform/flights-schemas'
import React from 'react'
import {
  getDurationInDays,
  getJourneyLayoverDuration,
} from '~/components/search-and-results/FlightCard/FlightCardUtils'
import { useDateMessageFormatters } from '~/hooks/useMessages'
import { formatMinutesToHours } from '~/utils/datetime'
import { useTranslations } from 'next-intl'
import { FlightCarrier } from './FlightCarrier'
import styles from './FlightCardSummary.module.scss'

type FlightSegment = Pick<
  FlightResult,
  'operatingCarrier' | 'marketingCarrier' | 'departureLocation' | 'arrivalLocation' | 'duration'
> & {
  cabinClass?: CabinClass
}

export type Journey = {
  totalDuration?: number | null
  stops: number
  departureLocation: string
  departureTime: string
  arrivalLocation: string
  arrivalTime: string
  departureTerminal?: string
  arrivalTerminal?: string
  flights: FlightSegment[]
  numberOfTravelers?: number
}

export type Props = {
  journey: Journey
  toggleDetails?: () => void
  variant?: string
  showDetailsButton: boolean
  showCabinClass?: boolean
  showNumberOfTravelers?: boolean
}

export function FlightCardSummary({
  journey,
  toggleDetails,
  variant,
  showDetailsButton,
  showCabinClass,
  showNumberOfTravelers,
}: Readonly<Props>) {
  const t = useTranslations('pages.order_history.flights')
  const { flights } = journey
  const { formatTime } = useDateMessageFormatters()
  const { layoverDuration, layoverLocation } = getJourneyLayoverDuration(journey)

  function getJourneyLabel() {
    const { stops } = journey
    if (!stops) {
      return t('direct_flight')
    }
    return `${stops} ${stops === 1 ? t('stop') : t('stops')}`
  }

  const journeyLabel = getJourneyLabel()
  const journeyDurationDays = getDurationInDays(journey.departureTime, journey.arrivalTime)

  const isCabinClassSameForAllFlights = flights.every(
    (flight) => flight.cabinClass === flights[0].cabinClass
  )

  return (
    <div className={styles.flightInfoContainer} data-testid="flight-card-summary">
      <div className={styles.summary_wrapper} data-testid="summary-wrapper">
        <div className={styles.departure} data-testid="departure">
          <p className={styles.departureTime}>{formatTime(journey.departureTime)}</p>
          <p className={styles.departureLocation}>{journey.departureLocation}</p>
          {journey.departureTerminal && (
            <p className={styles.departureTerminal}>
              {t('terminal')} {journey.departureTerminal}
            </p>
          )}
        </div>

        {journey.totalDuration ? (
          <div className={styles.duration_wrapper} data-testid="duration">
            <hr style={{ flex: 1 }} />
            <div className={styles.duration}>
              <div className={styles.iconWrapper}>
                <Icon iconName="clock" iconSize="1x" isDecorative />
                {formatMinutesToHours(journey.totalDuration)}
              </div>

              <div>
                <p className={styles.stops}>
                  {journeyLabel}

                  {!!layoverDuration && !!layoverLocation ? (
                    <span className={styles.layoverInfo}>
                      ({layoverLocation} - {formatMinutesToHours(layoverDuration)})
                    </span>
                  ) : null}
                </p>
              </div>
            </div>
            <hr style={{ flex: 1 }} />
          </div>
        ) : (
          <hr style={{ flex: 1 }} />
        )}

        <div data-testid="arrival" className={styles.arrival}>
          <p className={styles.arrivalTime}>{formatTime(journey.arrivalTime)}</p>
          <p>{journey.arrivalLocation}</p>

          {journeyDurationDays > 0 && (
            <p className={styles.next_day_arrival_notice}>
              {t('flight_landing_next_days', { x: journeyDurationDays })}
            </p>
          )}
          {journey.arrivalTerminal && (
            <p className={styles.arrivalTerminal}>
              {t('terminal')} {journey.arrivalTerminal}
            </p>
          )}
        </div>
      </div>

      <div data-testid="details-carrier" className={styles.summary_details}>
        <div className={styles.details_button_wrapper}>
          {showDetailsButton && (
            <Button
              onPress={toggleDetails}
              emphasis="secondary"
              size="sm"
              data-testid="toggle-flight-details"
            >
              {t('flight_details')}
            </Button>
          )}
        </div>

        {showNumberOfTravelers && journey.numberOfTravelers && (
          <div className={styles.travelers}>
            <Icon iconSize="1x" isDecorative color="secondary" iconName="user" pull="left" />
            <span>
              {t('number_of_travelers', { numberOfTravelers: journey.numberOfTravelers })}
            </span>
          </div>
        )}

        <div className={styles.carriers}>
          {flights.map((flight, i) => {
            const isFirstSegment = i === 0
            const shouldShowCabinClass =
              showCabinClass && (isFirstSegment || !isCabinClassSameForAllFlights)

            return (
              <FlightCarrier
                key={`${flight.departureLocation}-${flight.arrivalLocation}-${flight.marketingCarrier.flightNumber}`}
                flightNumber={flight.marketingCarrier.flightNumber}
                marketingCarrier={flight.marketingCarrier}
                operatingCarrier={flight.operatingCarrier}
                cabinClass={shouldShowCabinClass ? flight.cabinClass : undefined}
                variant={variant}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}
